import { SEOHead } from "@/components/SEOHead";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { Bot, Zap, Clock, BarChart3, CheckCircle, ArrowRight, RefreshCw, Database, Target, Brain, Mail, Phone, Calendar, AlertTriangle, TrendingUp, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";

const AICRMAutomation = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "NoQ5 AI CRM Automation",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "description": "Automate every CRM workflow with AI — from data entry to pipeline management. NoQ5 replaces manual CRM work completely.",
      "url": "https://noq5.ai/ai-crm-automation",
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
      "publisher": { "@type": "Organization", "name": "NoQ5", "url": "https://noq5.ai" }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is AI CRM automation?",
          "acceptedAnswer": { "@type": "Answer", "text": "AI CRM automation uses artificial intelligence to execute CRM tasks without human input — including data entry, contact enrichment, pipeline updates, activity logging, and deal forecasting. Unlike traditional automation rules, AI CRM automation understands context, learns from patterns, and acts autonomously." }
        },
        {
          "@type": "Question",
          "name": "How does AI automate CRM workflows?",
          "acceptedAnswer": { "@type": "Answer", "text": "AI automates CRM workflows by connecting to your email, calendar, calls, and meetings to automatically capture data, update records, enrich contacts, qualify leads, and generate insights — eliminating the need for manual data entry and admin work." }
        },
        {
          "@type": "Question",
          "name": "Can AI replace manual CRM data entry?",
          "acceptedAnswer": { "@type": "Answer", "text": "Yes. AI CRM agents like NoQ5 eliminate 95%+ of manual CRM data entry by automatically logging calls, emails, meetings, and notes directly into your CRM with the correct fields, contacts, and deal associations." }
        }
      ]
    }
  ];

  return (
    <Layout>
      <SEOHead
        title="CRM Automation: What It Is and Why It's Broken | NoQ5"
        description="CRM automation uses software to execute CRM tasks — data entry, enrichment, pipeline updates — without manual input. Here's what it means, why rule-based automation fails, and how autonomous CRM fixes it."
        keywords="CRM automation, what is CRM automation, AI CRM automation, automate CRM, autonomous CRM, CRM workflow automation, sales CRM automation, automated CRM"
        canonicalUrl="https://noq5.ai/ai-crm-automation"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center">
            <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-6">
              CRM AUTOMATION
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
              What Is CRM Automation?<br />
              <span className="text-primary">And Why Most Of It Is Broken.</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-4">
              <strong className="text-foreground">CRM automation</strong> is the use of software to execute CRM tasks — data entry, contact enrichment, pipeline updates, activity logging, lead routing, and forecasting — without manual input from sales reps. Traditional CRM automation relies on static "if-this-then-that" rules. Autonomous CRM, powered by AI agents, understands context and runs the workflow end-to-end.
            </p>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
              The catch: rule-based automation still leaves reps doing 5+ hours/week of admin. That's the gap NoQ5 closes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground" asChild>
                <Link to="/trial">See Autonomous CRM In Action <ArrowRight className="ml-2 h-5 w-5" /></Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/crm-agent">How The CRM Agent Works</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CRM Automation vs Autonomous CRM — the differentiator */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-4">CRM Automation vs Autonomous CRM</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Most "CRM automation" is workflow rules bolted onto Salesforce or HubSpot. Autonomous CRM is a different category — an AI agent that runs the CRM, not a trigger that fires when a rep remembers to update it.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-card p-8 rounded-xl border border-border">
              <h3 className="text-xl font-bold mb-4">Traditional CRM Automation</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex gap-2"><span className="text-destructive mt-1">✗</span> Static "if X then Y" rules</li>
                <li className="flex gap-2"><span className="text-destructive mt-1">✗</span> Requires admins to build and maintain workflows</li>
                <li className="flex gap-2"><span className="text-destructive mt-1">✗</span> Breaks when fields, stages, or processes change</li>
                <li className="flex gap-2"><span className="text-destructive mt-1">✗</span> Still depends on reps to log activity first</li>
                <li className="flex gap-2"><span className="text-destructive mt-1">✗</span> Examples: Salesforce Flow, HubSpot Workflows, Zapier</li>
              </ul>
            </div>
            <div className="bg-card p-8 rounded-xl border-2 border-primary/40">
              <h3 className="text-xl font-bold text-primary mb-4">Autonomous CRM (NoQ5)</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex gap-2"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /> AI agent that understands sales context</li>
                <li className="flex gap-2"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /> Zero rule-building — learns from your pipeline</li>
                <li className="flex gap-2"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /> Adapts to schema and process changes automatically</li>
                <li className="flex gap-2"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /> Captures activity directly from email, calls, meetings</li>
                <li className="flex gap-2"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" /> Runs alongside Salesforce, HubSpot, Pipedrive, Zoho</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-16 bg-destructive/5">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">Why Rule-Based CRM Automation Fails</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: AlertTriangle, title: "Rule-Based Automation Breaks", desc: "Traditional workflows need constant maintenance. When fields change, pipelines shift, or new reps join — your automation breaks silently." },
              { icon: Clock, title: "Reps Still Do Manual Work", desc: "Even with Zapier and workflows, reps still spend hours logging calls, writing notes, and updating deal stages. Automation didn't fix the human bottleneck." },
              { icon: Database, title: "Data Decays Instantly", desc: "Contact data goes stale within 90 days. Job titles change, companies merge, emails bounce. Without AI enrichment, your CRM is a graveyard." }
            ].map((item, i) => (
              <div key={i} className="bg-card p-6 rounded-xl border border-border">
                <item.icon className="h-10 w-10 text-destructive mb-4" />
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What AI CRM Automation Actually Means */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-4">What AI CRM Automation Actually Means</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Not rules. Not workflows. Not "if this then that." AI CRM automation means an intelligent agent that understands your sales process and executes CRM tasks autonomously.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { icon: Mail, title: "Email → CRM", desc: "Every email automatically logged, contacts created, deal stages updated based on conversation context." },
              { icon: Phone, title: "Call → CRM", desc: "Call summaries, action items, and sentiment analysis written directly to the right deal and contact records." },
              { icon: Calendar, title: "Meeting → CRM", desc: "Meeting notes, attendees, follow-ups, and next steps captured and pushed to CRM without lifting a finger." },
              { icon: Database, title: "Enrichment → CRM", desc: "Contact and company data enriched from 50+ sources automatically — job titles, company size, tech stack, funding." },
              { icon: Target, title: "Lead Scoring → CRM", desc: "Every lead scored based on engagement signals, firmographic data, and behavioral patterns. Updated in real-time." },
              { icon: BarChart3, title: "Pipeline → CRM", desc: "Deal stages advanced based on actual buyer engagement, not rep guesses. Forecasts built on evidence, not hope." }
            ].map((item, i) => (
              <div key={i} className="flex gap-4 p-6 bg-card rounded-xl border border-border">
                <item.icon className="h-8 w-8 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-lg mb-1">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before vs After */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">Before AI vs. After AI CRM Automation</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-card p-8 rounded-xl border-2 border-destructive/30">
              <h3 className="text-xl font-bold text-destructive mb-6">❌ Without AI Automation</h3>
              <ul className="space-y-3">
                {["5+ hours/week per rep on data entry", "30-50% of CRM data is outdated", "Deals slip because stages aren't updated", "Forecasts based on gut feeling", "New reps take months to learn CRM workflows", "Key data walks out when reps leave"].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-muted-foreground"><span className="text-destructive mt-1">✗</span>{item}</li>
                ))}
              </ul>
            </div>
            <div className="bg-card p-8 rounded-xl border-2 border-primary/30">
              <h3 className="text-xl font-bold text-primary mb-6">✅ With NoQ5 AI Automation</h3>
              <ul className="space-y-3">
                {["Zero manual data entry", "98% data accuracy maintained automatically", "Deal stages update based on real engagement", "Evidence-based forecasting", "New reps productive from day 1", "Institutional knowledge preserved forever"].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-muted-foreground"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Workflows */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">CRM Workflows NoQ5 Automates</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Lead Enrichment", link: "/crm-enrichment-agent", desc: "Auto-enrich every new lead with firmographic, technographic, and intent data." },
              { title: "Data Entry", link: "/crm-data-entry-automation", desc: "Eliminate manual logging of calls, emails, meetings, and notes." },
              { title: "Pipeline Management", link: "/pipeline-management-ai", desc: "AI-powered deal tracking, stage advancement, and risk alerts." },
              { title: "Lead Qualification", link: "/lead-qualification-ai", desc: "Automated BANT/MEDDIC scoring based on real engagement data." },
              { title: "Contact Sync", link: "/crm-agent", desc: "Keep contacts synchronized across email, CRM, and communication tools." },
              { title: "Forecasting", link: "/crm-agent", desc: "Evidence-based revenue forecasting that actually matches reality." }
            ].map((item, i) => (
              <Link key={i} to={item.link} className="bg-card p-6 rounded-xl border border-border hover:border-primary/50 transition-colors group">
                <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">{item.title}</h3>
                <p className="text-muted-foreground text-sm mb-3">{item.desc}</p>
                <span className="text-primary text-sm font-medium flex items-center gap-1">Learn more <ArrowRight className="h-4 w-4" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-4">Works With Every Major CRM</h2>
          <p className="text-center text-muted-foreground mb-12">NoQ5 connects to your existing CRM and starts automating in minutes — not months.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { name: "Salesforce", link: "/ai-agent-salesforce" },
              { name: "HubSpot", link: "/hubspot-ai-agent" },
              { name: "Pipedrive", link: "/pipedrive-ai-agent" },
              { name: "Zoho CRM", link: "/zoho-ai-agent" }
            ].map((crm, i) => (
              <Link key={i} to={crm.link} className="bg-card p-6 rounded-xl border border-border text-center hover:border-primary/50 transition-colors">
                <Bot className="h-10 w-10 text-primary mx-auto mb-3" />
                <h3 className="font-bold">{crm.name}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Your CRM Should Run Itself</h2>
          <p className="text-xl opacity-90 mb-8">Stop paying your sales team to be data entry clerks. Let AI handle the CRM so they can handle the revenue.</p>
          <Button size="lg" variant="secondary" asChild>
            <Link to="/trial">Start Free Trial <ArrowRight className="ml-2 h-5 w-5" /></Link>
          </Button>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-center mb-12">AI CRM Automation FAQ</h2>
          <div className="space-y-6">
            {[
              { q: "What is AI CRM automation?", a: "AI CRM automation uses intelligent agents to execute CRM tasks without human input — logging activities, enriching data, updating pipelines, and generating forecasts autonomously." },
              { q: "How is AI automation different from traditional CRM automation?", a: "Traditional automation uses static rules (if X then Y). AI automation understands context, learns from patterns, handles edge cases, and improves over time — no rule maintenance needed." },
              { q: "Does AI CRM automation work with Salesforce and HubSpot?", a: "Yes. NoQ5 integrates natively with Salesforce, HubSpot, Pipedrive, and other major CRMs. Setup takes minutes, not months." },
              { q: "How much time does AI CRM automation save?", a: "Teams typically save 5-8 hours per rep per week — that's 260-400 hours per year per rep redirected from admin work to selling." }
            ].map((item, i) => (
              <div key={i} className="bg-card p-6 rounded-xl border border-border">
                <h3 className="font-bold text-lg mb-2">{item.q}</h3>
                <p className="text-muted-foreground">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AICRMAutomation;
